import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { FloorPlanFileOrientation, useFloorPlanConfig } from '../_main/FloorPlanConfigStep.context'
import React, { useCallback, useMemo } from 'react'
import { usePurchaseFlowConfig, usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts'

import { BinaryButtonSwitch } from 'components/common/BinaryButtonSwitch'
import Box from '@mui/material/Box'
import { FloorPlanLogoUpload } from 'components/pages/PurchaseFlow/common/FloorPlanLogoUpload'
import { FloorPlanToggleSetting } from 'components/common/FloorPlanControls'
import { GRAY_900 } from 'constants/styling/theme'
import { InputWrap } from 'components/common/InputWrap'
import { Label } from 'components/common/Label'
import { Language } from 'translations/Language'
import { MUICheckCard } from 'components/common/MUICards'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import { Stack } from '@mui/material'
import { SystemMessage } from 'components/common/SystemMessage'
import { ToggleFilterValue } from 'components/common/ToggleFilter'
import Typography from '@mui/material/Typography'
import { isEditingCategory } from 'utils/validators'
import { useDraggingFile } from 'utils/hooks'
import { useTranslation } from 'react-i18next'

import { FloorPlanFormat } from 'constants/floorPlan'

export { FloorPlanFormat }

/**
 * @component
 * FloorPlanLayout displays the layout options for floor plans.
 * 
 * @example
 * <FloorPlanLayout />
 */
export const FloorPlanLayout: React.FC = () => {
  const { t } = useTranslation(['purchase_flow', 'order', 'step_address', 'enums'])

  const {
    showRoomNames,
    setShowRoomNames,
    showScalebar,
    setShowScalebar,
    showCompass,
    setShowCompass,
    outputLanguage,
    setOutputLanguage,
    floorPlanFormat,
    setFloorPlanFormat,
    isActiveDimensions,
    setIsActiveDimensions,
    showDimensionChain,
    setShowDimensionChain,
    showRoomArea,
    setShowRoomArea,
    isActiveLogo,
    setIsActiveLogo,
    showDisclaimer,
    disclaimerText,
    setShowDisclaimer,
    setDisclaimerText,
    fileOrientation,
    setFileOrientation,
    isMultiStoryPlan,
    setIsMultiStoryPlan,
  } = useFloorPlanConfig()

  const { selectedCategory } = usePurchaseFlowConfig()
  const { floorPlanProduct } = usePurchaseFlowProducts()

  const isDraggingFile = useDraggingFile()

  const formatToggleValues: ToggleFilterValue<FloorPlanFormat>[] = useMemo(
    () => {
      let availableFormat: FloorPlanFormat[] = [FloorPlanFormat.JPG]

      // available format for 2D
      availableFormat.push(FloorPlanFormat.PDF)

      return availableFormat.map((format) => {
        return {
          value: format,
          displayName: format,
        }
      })
    }, []
  )

  const toggleFormat = useCallback((format: FloorPlanFormat) => {
    setFloorPlanFormat((prev) => {
      const updated = { ...prev }
      const newSelection = updated[format]

      if (newSelection) delete updated[format]
      else updated[format] = format

      return updated
    })
  }, [setFloorPlanFormat])


  return (
    <SectionedBorderBox title={t('floor_plan_step.layout.title')}>
      <Box display="flex" flexDirection="column" gap={1.6}>

        <MUICheckCard
          checked={showRoomNames}
          onCheck={() => setShowRoomNames((prevState) => !prevState)}
          expandableContent={<SystemMessage message={t(!!selectedCategory && isEditingCategory(selectedCategory) ? 'floor_plan_step.layout.add_room_name_message' : 'floor_plan_step.layout.add_room_name_message_capture')} />}
        >
          <Typography variant='text-md' fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.add_room_name')}
          </Typography>
        </MUICheckCard>

        <MUICheckCard
          checked={isActiveDimensions}
          onCheck={() => setIsActiveDimensions((prevState) => !prevState)}
          expandableContent={(
            <Stack gap=".8rem">

              <FloorPlanToggleSetting
                title={t('floor_plan_step.layout.showcase_dimension_chains')}
                value={showDimensionChain}
                onChange={setShowDimensionChain}
              />

              {!!showDimensionChain &&
                <SystemMessage message={t('floor_plan_step.layout.dimension_chains_message')} />
              }

              <FloorPlanToggleSetting
                title={t('floor_plan_step.layout.showcase_room_area')}
                value={showRoomArea}
                onChange={setShowRoomArea}
              />

            </Stack>
          )}
        >
          <Stack gap=".6rem" flexDirection="row" alignItems="center">
            <Typography variant='text-md' fontWeight={600} color={GRAY_900}>
              {t('floor_plan_step.layout.add_dimensions')}
            </Typography>
          </Stack>
        </MUICheckCard>

        <MUICheckCard
          checked={showScalebar}
          onCheck={() => setShowScalebar((prevState) => !prevState)}
        >
          <Typography variant='text-md' fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.add_scalebar')}
          </Typography>
        </MUICheckCard>

        <MUICheckCard
          checked={showCompass}
          onCheck={() => setShowCompass((prevState) => !prevState)}
          expandableContent={<SystemMessage message={t('floor_plan_step.layout.compass_info_message')} />}
        >
          <Typography variant='text-md' fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.add_compass')}
          </Typography>
        </MUICheckCard>

        <MUICheckCard
          checked={isActiveLogo}
          onCheck={() => setIsActiveLogo((prevState) => !prevState)}
          expandableContent={!!floorPlanProduct &&
            <FloorPlanLogoUpload
              isDraggingFile={isDraggingFile}
            />
          }
        >
          <Typography variant='text-md' fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.add_company_logo')}
          </Typography>


        </MUICheckCard>

        <MUICheckCard
          checked={showDisclaimer}
          onCheck={() => setShowDisclaimer((prevState) => !prevState)}
          expandableContent={
            <Box>
              <InputWrap label={''} error={showDisclaimer && disclaimerText === '' ? t('floor_plan_step.layout.disclaimer_error') : undefined}>
                <textarea
                  placeholder={t('floor_plan_step.layout.disclaimer_placeholder')}
                  value={disclaimerText}
                  onChange={(e) => setDisclaimerText(e.target.value)}
                  maxLength={200}>
                </textarea>
              </InputWrap>
            </Box>
          }
        >
          <Typography variant='text-md' fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.disclaimer')}
          </Typography>
        </MUICheckCard>

        <SectionedBorderBox title={t('floor_plan_step.layout.format')}>
          <Stack gap={1}>

            <Stack gap={.5}>
              <Label text={t('floor_plan_step.layout.export')} required />
              <SelectableButtonsGrid
                showCheckbox
                preventUnselect
                activeButtonClickable
                values={formatToggleValues}
                selectedValue={Object.values(floorPlanFormat)}
                onSelect={format => toggleFormat(format)}
                onUnselect={format => toggleFormat(format)}
              />
            </Stack>

            <Stack gap={.5}>
              <Label text={t('floor_plan_step.layout.orientation')} required />
              <SelectableButtonsGrid
                preventUnselect
                activeButtonClickable
                showCheckbox
                values={Object.values(FloorPlanFileOrientation).map((orientation) => ({
                  value: orientation,
                  displayName: t(`enums:floor_plan_orientation.${orientation}`),
                }))}
                selectedValue={Array.from(fileOrientation)}
                onSelect={orientation => setFileOrientation((prev) => new Set([...prev, orientation]))}
                onUnselect={orientation => setFileOrientation((prev) => {
                  const newSet = new Set([...prev])
                  newSet.delete(orientation)

                  return newSet
                })}
              />
            </Stack>

          </Stack>
        </SectionedBorderBox>

        <SectionedBorderBox title={t('floor_plan_step.layout.multi_story_title')}>
          <Stack gap={.5}>
            <Label text={t('floor_plan_step.layout.multi_story_label')} required />
            <BinaryButtonSwitch
              value={isMultiStoryPlan}
              preventUnselect
              onChange={(isMultiStory) => setIsMultiStoryPlan(isMultiStory ?? false)}
            />
          </Stack>
        </SectionedBorderBox>

        <SectionedBorderBox title={t('floor_plan_step.layout.output_lang')}>
          <Box width="50%">

            <MUIDropdown
              button={(isOpen, action) => (
                <MUIDropdownInput
                  readOnly
                  type={DropdownInputTypes.SELECTOR}
                  value={t(`language:${outputLanguage.toUpperCase()}`, outputLanguage)}
                  onClick={action}
                />
              )}
            >
              {Object.values(Language)?.map(lang => (
                <MUIDropdownItem key={lang} onClick={() => setOutputLanguage(lang)}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                    {t(`language:${lang.toUpperCase()}`, lang)}
                  </Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>

          </Box>
        </SectionedBorderBox>

      </Box>
    </SectionedBorderBox>
  )
}
